import React, { useState, useEffect } from 'react';
import DefaultLogo from '../../../Assets/img/default-logo.png';

const RobustLogo = ({ 
  src, 
  alt = "Logo", 
  className = "", 
  style = {},
  fallbackService = true,
  onErrorLog = true
}) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [loadError, setLoadError] = useState(false);
  const [fallbackAttempt, setFallbackAttempt] = useState(0);
  console.log("RobustLogo", src);
  // Validate URL function
  const isValidUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.startsWith('http://') || 
           url.startsWith('https://') || 
           url.startsWith('data:') || 
           url.startsWith('blob:');
  };

  // Extract domain from URL
  const extractDomain = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      
      // Clean the URL - remove paths and query parameters
      let cleanUrl = url;
      if (url.startsWith('http')) {
        const urlObj = new URL(url);
        cleanUrl = urlObj.origin;
      }
      
      // Handle URLs without protocol
      const urlWithProtocol = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;
      const domain = new URL(urlWithProtocol).hostname;
      return domain.replace('www.', '');
    } catch (e) {
      return null;
    }
  };

  // Get favicon from Google's service
  const getGoogleFaviconUrl = (url) => {
    const domain = extractDomain(url);
    return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null;
  };

  // Get favicon from icon.horse service
  const getIconHorseUrl = (url) => {
    const domain = extractDomain(url);
    return domain ? `https://icon.horse/icon/${domain}` : null;
  };

  // Determine next fallback URL based on attempt count
  const getNextFallbackUrl = () => {
    if (!src)  {
      return DefaultLogo;
    }

    switch (fallbackAttempt) {
      case 0: // First try: original URL
        return src;
      case 1: // Second try: convert .ico to .png if applicable
        return src.includes('.ico') ? src.replace('.ico', '.png') : src;
      case 2: // Third try: Google favicon service
        return getGoogleFaviconUrl(src);
      case 3: // Fourth try: icon.horse service
        return getIconHorseUrl(src);
      default: // Finally: default logo
        return DefaultLogo;
    }
  };

  // Reset when src changes
  useEffect(() => {
    console.log("RobustLogo received src:", src);
    
    if (!src) {
      console.log("No src provided, using default logo");
      setCurrentSrc(DefaultLogo);
      setLoadError(true);
    } else if (typeof src !== 'string') {
      console.log("Invalid src type:", typeof src);
      setCurrentSrc(DefaultLogo);
      setLoadError(true);
    } else if (!isValidUrl(src) && !src.startsWith('/') && !src.startsWith('./')) {
      console.log("Invalid URL format, using default:", src);
      setCurrentSrc(DefaultLogo);
      setLoadError(true);
    } else {
      setCurrentSrc(src);
      setLoadError(false);
      setFallbackAttempt(0);
      
      if (onErrorLog) {
        console.log("Loading logo from:", {
          url: src,
          isValid: isValidUrl(src),
          domain: extractDomain(src)
        });
      }
    }
  }, [src, onErrorLog]);

  const handleError = () => {
    if (fallbackAttempt < 4) {
      // Try next fallback
      const nextAttempt = fallbackAttempt + 1;
      setFallbackAttempt(nextAttempt);
      const nextUrl = getNextFallbackUrl();
      setCurrentSrc(nextUrl);
      
      if (onErrorLog) {
        console.log(`Logo load failed (attempt ${fallbackAttempt + 1}), trying fallback:`, {
          failedUrl: currentSrc,
          nextAttempt: nextAttempt,
          nextUrl: nextUrl
        });
      }
    } else {
      // All fallbacks failed, use default logo
      setLoadError(true);
      setCurrentSrc(DefaultLogo);
      
      if (onErrorLog) {
        console.error("All logo fallbacks failed for:", src);
        console.error("Final fallback to default logo");
      }
    }
  };

  return (
    <img
      src={currentSrc || DefaultLogo}
      alt={alt}
      className={className}
      style={{
        ...style,
        opacity: loadError ? 0.7 : 1,
      }}
      onError={handleError}
      // crossOrigin="anonymous"
      loading="lazy"
    />
  );
};

export default RobustLogo;